import React, { useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { hooks } from '../hooks';
import { text } from '../text';
import { utils } from '../utils';
import { theme } from '../constants';
import { components } from '../components';
import { useLanguage } from '../context/LanguageContext';
import axiosInstance from '../services/axiosInstance';
import {
  MdCheckCircle, MdCancel, MdAccessTime,
  MdAttachMoney, MdSubject, MdError, MdAttachFile, MdClose, MdFileDownload,
} from 'react-icons/md';
import { FaFilePdf, FaFileImage, FaFileAlt } from 'react-icons/fa';

const CheckCircleIcon = MdCheckCircle as any;
const CancelIcon = MdCancel as any;
const PendingIcon = MdAccessTime as any;
const MoneyIcon = MdAttachMoney as any;
const SubjectIcon = MdSubject as any;
const ErrorIcon = MdError as any;
const AttachFileIcon = MdAttachFile as any;
const CloseIcon = MdClose as any;
const DownloadIcon = MdFileDownload as any;
const FilePdfIcon = FaFilePdf as any;
const FileImageIcon = FaFileImage as any;
const FileAltIcon = FaFileAlt as any;

export const LoanDetail: React.FC = () => {
  const { state } = useLocation();
  const navigate = hooks.useAppNavigate();
  const { t, language } = useLanguage();
  const tl = (t as any).loanDetail || {};

  const [loan, setLoan] = useState<any>(state?.loan);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [previewPdf, setPreviewPdf] = useState<{ url: string; name: string } | null>(null);
  const [imgModal, setImgModal] = useState<{ url: string; name: string } | null>(null);
  const [imgZoom, setImgZoom] = useState(1);
  const [imgTranslate, setImgTranslate] = useState({ x: 0, y: 0 });

  const pinchRef   = useRef<{ startDist: number; startZoom: number } | null>(null);
  const dragRef    = useRef<{ startX: number; startY: number; startTx: number; startTy: number } | null>(null);
  const lastTapRef = useRef(0);
  const hasPinchedRef = useRef(false); // true saat gesture aktif adalah pinch (bukan tap)

  const openImgModal = (url: string, name: string) => {
    setImgModal({ url, name });
    setImgZoom(1);
    setImgTranslate({ x: 0, y: 0 });
  };

  const closeImgModal = () => {
    setImgModal(null);
    setImgZoom(1);
    setImgTranslate({ x: 0, y: 0 });
  };

  const handleImgTouchStart = (e: React.TouchEvent) => {
    e.stopPropagation();
    if (e.touches.length === 2) {
      hasPinchedRef.current = true;
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      pinchRef.current = { startDist: Math.hypot(dx, dy), startZoom: imgZoom };
      dragRef.current = null;
    } else if (e.touches.length === 1) {
      dragRef.current = {
        startX: e.touches[0].clientX, startY: e.touches[0].clientY,
        startTx: imgTranslate.x, startTy: imgTranslate.y,
      };
    }
  };

  const handleImgTouchMove = (e: React.TouchEvent) => {
    e.stopPropagation();
    if (e.touches.length === 2 && pinchRef.current) {
      e.preventDefault();
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const newZoom = Math.min(5, Math.max(1, (Math.hypot(dx, dy) / pinchRef.current.startDist) * pinchRef.current.startZoom));
      setImgZoom(newZoom);
      if (newZoom <= 1) setImgTranslate({ x: 0, y: 0 });
    } else if (e.touches.length === 1 && dragRef.current && imgZoom > 1) {
      const dx = e.touches[0].clientX - dragRef.current.startX;
      const dy = e.touches[0].clientY - dragRef.current.startY;
      setImgTranslate({ x: dragRef.current.startTx + dx, y: dragRef.current.startTy + dy });
    }
  };

  const handleImgTouchEnd = (e: React.TouchEvent) => {
    e.stopPropagation();
    // Semua jari sudah dilepas — reset pinch state
    if (e.touches.length === 0) {
      pinchRef.current = null;
      dragRef.current = null;
      const wasPinch = hasPinchedRef.current;
      hasPinchedRef.current = false;

      // Hanya proses double-tap jika gesture ini BUKAN pinch
      if (!wasPinch && e.changedTouches.length === 1) {
        const now = Date.now();
        const dt = now - lastTapRef.current;
        lastTapRef.current = now;
        if (dt < 300 && dt > 0) {
          setImgZoom(z => {
            const next = z > 1 ? 1 : 2.5;
            if (next <= 1) setImgTranslate({ x: 0, y: 0 });
            return next;
          });
        }
      }
    }
  };

  const imageBaseUrl = process.env.REACT_APP_IMAGE_BASE_URL || 'https://apiteratasks.teralintas.com';

  const getAttachmentMeta = (url: string) => {
    const fileName = url.split(/[/\\]/).pop() || 'Attachment';
    const ext = fileName.split('.').pop()?.toLowerCase() || '';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return { fileName, type: 'image', icon: <FileImageIcon size={20} color="#3B82F6" /> };
    if (ext === 'pdf') return { fileName, type: 'pdf', icon: <FilePdfIcon size={20} color="#EF4444" /> };
    return { fileName, type: 'file', icon: <FileAltIcon size={20} color="#9CA3AF" /> };
  };
  const [alertConfig, setAlertConfig] = useState<{
    isOpen: boolean; title: string; message: string;
    type: 'success' | 'error'; onClose?: () => void;
  }>({ isOpen: false, title: '', message: '', type: 'success' });

  const locale = language === 'id' ? 'id-ID' : 'en-US';

  const showAlert = (title: string, message: string, type: 'success' | 'error' = 'success', onClose?: () => void) => {
    setAlertConfig({ isOpen: true, title, message, type, onClose });
  };
  const hideAlert = () => {
    const cb = alertConfig.onClose;
    setAlertConfig(p => ({ ...p, isOpen: false }));
    if (cb) cb();
  };

  if (!loan) {
    return (
      <div id='screen' style={{ ...utils.flexCenter(), height: '100vh', flexDirection: 'column' }}>
        <components.Header title={tl.title || 'Detail Kasbon'} goBack={true} />
        <text.T16>{'Data tidak ditemukan.'}</text.T16>
        <components.Button title={t.common?.goBack || 'Kembali'} onClick={() => navigate(-1)} containerStyle={{ marginTop: 20, width: 200 }} />
      </div>
    );
  }

  const getStatusConfig = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'approved': return { color: '#10B981', bg: '#D1FAE5', icon: <CheckCircleIcon size={30} />, label: tl.approved || 'Disetujui' };
      case 'rejected': return { color: '#EF4444', bg: '#FEE2E2', icon: <CancelIcon size={30} />, label: tl.rejected || 'Ditolak' };
      default: return { color: '#F59E0B', bg: '#FEF3C7', icon: <PendingIcon size={30} />, label: tl.pending || 'Menunggu' };
    }
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(amount);

  const formatDatetime = (d: string) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const tenorUnitLabel = (unit: string) => unit === 'weeks' ? (tl.weeks || 'Minggu') : (tl.months || 'Bulan');

  const hasApprovedAmount = loan.approved_amount && parseFloat(loan.approved_amount) !== parseFloat(loan.amount);
  const hasApprovedTenor = loan.approved_tenor && (loan.approved_tenor !== loan.tenor || loan.approved_tenor_unit !== loan.tenor_unit);

  const handleCancel = async () => {
    setShowConfirm(false);
    setIsLoading(true);
    try {
      await axiosInstance.delete(`/loans/${loan.id}`);
      showAlert(t.common?.success || 'Berhasil', tl.cancelSuccess || 'Pengajuan kasbon berhasil dibatalkan.', 'success', () => navigate(-1));
    } catch (err: any) {
      showAlert(t.common?.error || 'Error', err?.response?.data?.message || tl.cancelError || 'Gagal membatalkan pengajuan.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const status = getStatusConfig(loan.status);

  const DetailRow = ({ icon, label, value, valueStyle }: { icon: React.ReactNode; label: string; value: string; valueStyle?: React.CSSProperties }) => (
    <div style={{ display: 'flex', gap: 12 }}>
      <div style={{ color: '#9CA3AF', marginTop: 2, flexShrink: 0 }}>{icon}</div>
      <div style={{ flex: 1 }}>
        <text.T12 style={{ color: '#6B7280', marginBottom: 2 }}>{label}</text.T12>
        <text.T16 style={{ fontWeight: '500', color: theme.colors.mainDark, ...valueStyle }}>{value}</text.T16>
      </div>
    </div>
  );

  return (
    <div id='screen' style={{ backgroundColor: '#F9FAFB', minHeight: '100vh' }}>
      <components.Header title={tl.title || 'Detail Kasbon / Pinjaman'} goBack={true} />

      <main className='container' style={{ marginTop: 60, paddingBottom: 120 }}>

        {/* Status Banner */}
        <div style={{
          backgroundColor: status.bg, padding: '20px 20px',
          borderRadius: 16, marginBottom: 16,
          display: 'flex', alignItems: 'center', gap: 16,
          border: `1px solid ${status.color}30`,
        }}>
          <div style={{ color: status.color }}>{status.icon}</div>
          <div>
            <text.T14 style={{ color: status.color, opacity: 0.8, marginBottom: 2 }}>{tl.currentStatus || 'Status Pengajuan'}</text.T14>
            <text.H4 style={{ color: status.color }}>{status.label}</text.H4>
          </div>
        </div>

        {/* Loan Details */}
        <div style={{ backgroundColor: '#fff', borderRadius: 16, border: '1px solid #F3F4F6', padding: 20, marginBottom: 12 }}>
          <text.H5 style={{ marginBottom: 16, borderBottom: '1px solid #F3F4F6', paddingBottom: 10 }}>
            {tl.loanInfo || 'Detail Pinjaman'}
          </text.H5>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Amount */}
            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ color: '#9CA3AF', marginTop: 2, flexShrink: 0 }}><MoneyIcon size={20} /></div>
              <div style={{ flex: 1 }}>
                <text.T12 style={{ color: '#6B7280', marginBottom: 2 }}>{tl.amount || 'Jumlah Pinjaman'}</text.T12>
                <text.T16 style={{ fontSize: 18, fontWeight: '700', color: '#10B981' }}>
                  {formatCurrency(parseFloat(hasApprovedAmount ? loan.approved_amount : loan.amount))}
                </text.T16>
                {hasApprovedAmount && (
                  <text.T12 style={{ color: '#9CA3AF', textDecoration: 'line-through', marginTop: 2 }}>
                    {formatCurrency(parseFloat(loan.amount))} ({tl.requested || 'diminta'})
                  </text.T12>
                )}
              </div>
            </div>

            {/* Tenor */}
            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ color: '#9CA3AF', marginTop: 2, flexShrink: 0 }}><MoneyIcon size={20} /></div>
              <div style={{ flex: 1 }}>
                <text.T12 style={{ color: '#6B7280', marginBottom: 2 }}>{tl.tenor || 'Tenor'}</text.T12>
                <text.T16 style={{ fontWeight: '500', color: theme.colors.mainDark }}>
                  {hasApprovedTenor
                    ? `${loan.approved_tenor} ${tenorUnitLabel(loan.approved_tenor_unit)}`
                    : `${loan.tenor} ${tenorUnitLabel(loan.tenor_unit)}`}
                </text.T16>
                {hasApprovedTenor && (
                  <text.T12 style={{ color: '#9CA3AF', textDecoration: 'line-through', marginTop: 2 }}>
                    {loan.tenor} {tenorUnitLabel(loan.tenor_unit)} ({tl.requested || 'diminta'})
                  </text.T12>
                )}
              </div>
            </div>

            {loan.reason ? (
              <DetailRow
                icon={<SubjectIcon size={20} />}
                label={tl.reason || 'Alasan'}
                value={loan.reason}
              />
            ) : null}
          </div>
        </div>

        {/* Admin Note (if approved/rejected) */}
        {loan.admin_note && (
          <div style={{ backgroundColor: '#fff', borderRadius: 16, border: '1px solid #F3F4F6', padding: 20, marginBottom: 12 }}>
            <text.H5 style={{ marginBottom: 16, borderBottom: '1px solid #F3F4F6', paddingBottom: 10 }}>
              {tl.adminNote || 'Catatan Admin'}
            </text.H5>
            <text.T16 style={{ color: theme.colors.mainDark, lineHeight: 1.6 }}>{loan.admin_note}</text.T16>
          </div>
        )}

        {/* Attachment */}
        {loan.attachment_url && (() => {
          const fullUrl = `${imageBaseUrl}${loan.attachment_url}`;
          const { fileName, type, icon } = getAttachmentMeta(loan.attachment_url);
          return (
            <div style={{ backgroundColor: '#fff', borderRadius: 16, border: '1px solid #F3F4F6', padding: 20, marginBottom: 12 }}>
              <text.H5 style={{ marginBottom: 12, borderBottom: '1px solid #F3F4F6', paddingBottom: 10 }}>
                {tl.attachment || 'Dokumen Pendukung'}
              </text.H5>
              <div
                onClick={() => type === 'pdf' ? setPreviewPdf({ url: fullUrl, name: fileName }) : type === 'image' ? openImgModal(fullUrl, fileName) : window.open(fullUrl, '_blank')}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '12px 14px', borderRadius: 10,
                  border: '1px solid #E5E7EB', cursor: 'pointer',
                  backgroundColor: '#FAFAFA',
                }}
              >
                {icon}
                <text.T14 style={{ flex: 1, color: theme.colors.mainDark, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {fileName}
                </text.T14>
                <DownloadIcon size={18} color="#9CA3AF" onClick={(e: any) => { e.stopPropagation(); utils.downloadFile(fullUrl, fileName); }} />
              </div>
            </div>
          );
        })()}

        {/* Timestamp */}
        <div style={{ padding: '4px 4px', marginBottom: 8 }}>
          <text.T12 style={{ color: '#9CA3AF' }}>
            {tl.submittedAt || 'Diajukan'}: {formatDatetime(loan.created_at)}
          </text.T12>
          {loan.updated_at && loan.updated_at !== loan.created_at && (
            <text.T12 style={{ color: '#9CA3AF', display: 'block', marginTop: 4 }}>
              {tl.updatedAt || 'Diperbarui'}: {formatDatetime(loan.updated_at)}
            </text.T12>
          )}
        </div>
      </main>

      {/* Cancel Button */}
      {loan.status === 'pending' && (
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0,
          backgroundColor: '#fff', padding: '16px 24px 32px',
          boxShadow: '0 -4px 20px rgba(0,0,0,0.05)', zIndex: 10,
        }}>
          <div className='container' style={{ padding: 0, maxWidth: 650, margin: '0 auto' }}>
            <components.Button
              title={isLoading ? (t.common?.loading || 'Memproses...') : (tl.cancelBtn || 'Batalkan Pengajuan')}
              style={{ backgroundColor: '#F3F4F6', color: '#4B5563', border: '1px solid #E5E7EB' }}
              onClick={() => setShowConfirm(true)}
            />
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      {showConfirm && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 5000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ backgroundColor: '#fff', borderRadius: 16, padding: 24, width: '100%', maxWidth: 300, textAlign: 'center' }}>
            <text.H4 style={{ marginBottom: 10 }}>{tl.confirmTitle || 'Konfirmasi'}</text.H4>
            <text.T16 style={{ color: '#6B7280', marginBottom: 24 }}>{tl.cancelConfirm || 'Yakin ingin membatalkan pengajuan kasbon ini?'}</text.T16>
            <div style={{ display: 'flex', gap: 10 }}>
              <components.Button title={t.common?.cancel || 'Tidak'} colorScheme="light" containerStyle={{ flex: 1 }} onClick={() => setShowConfirm(false)} />
              <components.Button title={tl.cancelBtnConfirm || 'Ya, Batalkan'} containerStyle={{ flex: 1 }} onClick={handleCancel} />
            </div>
          </div>
        </div>
      )}

      {/* Image Viewer Modal */}
      {imgModal && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 7000,
            backgroundColor: 'rgba(0,0,0,0.92)',
            backdropFilter: 'blur(12px)',
            display: 'flex', flexDirection: 'column',
          }}
          onClick={closeImgModal}
        >
          {/* Close button */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '16px 16px 0' }} onClick={e => e.stopPropagation()}>
            <div
              onClick={closeImgModal}
              style={{
                width: 40, height: 40, borderRadius: 20,
                backgroundColor: 'rgba(255,255,255,0.12)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              <CloseIcon size={22} color="white" />
            </div>
          </div>

          {/* Image — klik di area hitam kosong (luar gambar) menutup modal */}
          <div
            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}
          >
            <img
              src={imgModal.url}
              alt={imgModal.name}
              draggable={false}
              onClick={e => e.stopPropagation()}
              style={{
                maxWidth: '100%', maxHeight: '100%',
                objectFit: 'contain',
                borderRadius: imgZoom <= 1 ? 8 : 0,
                transform: `scale(${imgZoom}) translate(${imgTranslate.x / imgZoom}px, ${imgTranslate.y / imgZoom}px)`,
                transformOrigin: 'center center',
                transition: imgZoom <= 1 ? 'transform 0.2s ease' : 'none',
                touchAction: 'none',
                userSelect: 'none',
                cursor: imgZoom > 1 ? 'grab' : 'default',
              }}
              onTouchStart={handleImgTouchStart}
              onTouchMove={handleImgTouchMove}
              onTouchEnd={handleImgTouchEnd}
            />
          </div>

          {/* Filename */}
          <div style={{ padding: '12px 20px 40px', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
            <text.T14 style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }} numberOfLines={1}>{imgModal.name}</text.T14>
          </div>
        </div>
      )}

      {/* PDF Viewer Modal */}
      {previewPdf && (
        <div
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 7000, display: 'flex', flexDirection: 'column', padding: 16 }}
          onClick={() => setPreviewPdf(null)}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }} onClick={(e) => e.stopPropagation()}>
            <text.H5 style={{ color: 'white', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: 12 }}>{previewPdf.name}</text.H5>
            <div onClick={() => setPreviewPdf(null)} style={{ cursor: 'pointer', backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: '50%', padding: 6, display: 'flex' }}>
              <CloseIcon size={22} color="white" />
            </div>
          </div>
          <div style={{ flex: 1, backgroundColor: 'white', borderRadius: 10, overflow: 'hidden' }} onClick={(e) => e.stopPropagation()}>
            <object data={`${previewPdf.url}#toolbar=1&navpanes=0`} type="application/pdf" width="100%" height="100%">
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', padding: 20 }}>
                <text.T14 style={{ textAlign: 'center', marginBottom: 16 }}>
                  {(t as any).common?.pdfPreviewNotSupported || 'PDF Preview tidak didukung di browser ini.'}
                </text.T14>
                <components.Button title={tl.download || 'Unduh Berkas'} onClick={() => utils.downloadFile(previewPdf.url, previewPdf.name)} />
              </div>
            </object>
          </div>
        </div>
      )}

      {/* Alert Modal */}
      {alertConfig.isOpen && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 6000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ backgroundColor: '#fff', borderRadius: 16, padding: 24, width: '100%', maxWidth: 300, textAlign: 'center' }}>
            <div style={{ marginBottom: 12 }}>
              {alertConfig.type === 'success' ? <CheckCircleIcon size={48} color="#10B981" /> : <ErrorIcon size={48} color="#EF4444" />}
            </div>
            <text.H4 style={{ marginBottom: 8 }}>{alertConfig.title}</text.H4>
            <text.T16 style={{ color: '#6B7280', marginBottom: 20 }}>{alertConfig.message}</text.T16>
            <components.Button title={t.common?.ok || 'OK'} onClick={hideAlert} />
          </div>
        </div>
      )}
    </div>
  );
};
